import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Warning as WarningIcon,
  RestartAlt as RestartIcon,
  PowerOff as ShutdownIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { getAuthHeaders  } from '../utils/authUtils';
import { apiFetch } from '../utils/apiConfig';

const ScheduledActionNotification = () => {
  const [scheduledAction, setScheduledAction] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [timerVisible, setTimerVisible] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [backendDown, setBackendDown] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [showReconnectedMessage, setShowReconnectedMessage] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  // Polling für System-Status
  useEffect(() => {
    const checkSystemStatus = async () => {
      try {
        const authHeaders = getAuthHeaders();
        if (!authHeaders.Authorization) return; // Nur wenn eingeloggt

        const response = await apiFetch('/system-status');

        if (response.ok) {
          const data = await response.json();
          
          // Backend ist wieder verfügbar (nur zeigen wenn es tatsächlich down war)
          if (backendDown && initialCheckDone) {
            setBackendDown(false);
            setReconnecting(false);
            setShowReconnectedMessage(true);
            
            // "Wieder verfügbar" Nachricht nach 3 Sekunden ausblenden
            setTimeout(() => {
              setShowReconnectedMessage(false);
            }, 3000);
          } else if (!initialCheckDone) {
            // Erster Check - kein "wieder verfügbar" zeigen
            setBackendDown(false);
            setInitialCheckDone(true);
          }
          
          if (data.scheduled_action) {
            const action = data.scheduled_action;
            
            // Neue geplante Aktion erkannt
            if (!scheduledAction || scheduledAction.scheduled_time !== action.scheduled_time) {
              setScheduledAction(action);
              setDialogOpen(true);
              setTimerVisible(false);
            }
            
            // Update verbleibende Zeit
            setRemainingSeconds(action.remaining_seconds);
          } else {
            // Keine geplante Aktion mehr
            setScheduledAction(null);
            setDialogOpen(false);
            setTimerVisible(false);
          }
        } else {
          throw new Error('Backend nicht erreichbar');
        }
      } catch (error) {
        console.error('Error checking system status:', error);
        
        // Backend ist nicht erreichbar
        if (!backendDown) {
          setBackendDown(true);
          setReconnecting(true);
          setScheduledAction(null);
          setDialogOpen(false);
          setTimerVisible(false);
        }
        
        // Initial check abgeschlossen, auch wenn Backend nicht verfügbar
        if (!initialCheckDone) {
          setInitialCheckDone(true);
        }
      }
    };

    // Initial check
    checkSystemStatus();

    // Häufigere Abfrage wenn Backend down ist
    const interval = setInterval(checkSystemStatus, backendDown ? 5000 : 30000);

    return () => clearInterval(interval);
  }, [scheduledAction, backendDown]);

  // Timer Update jede Sekunde wenn Timer sichtbar
  useEffect(() => {
    if (timerVisible && remainingSeconds > 0) {
      const timer = setInterval(() => {
        setRemainingSeconds(prev => {
          if (prev <= 1) {
            // Zeit abgelaufen - Backend wird neu gestartet
            setTimerVisible(false);
            setBackendDown(true);
            setReconnecting(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timerVisible, remainingSeconds]);

  const handleUnderstood = () => {
    setDialogOpen(false);
    setTimerVisible(true);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (!scheduledAction) return null;

  const actionText = scheduledAction.action === 'restart' ? 'Neustart' : 'Herunterfahren';
  const actionVerb = scheduledAction.action === 'restart' ? 'neustartet' : 'herunterfährt';

  return (
    <>
      {/* Benachrichtigungs-Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => {}} // Nicht schließbar
        maxWidth="md" 
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            <Typography variant="h6" color="error">
              Achtung! System-{actionText} geplant
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <DialogContentText>
              Für das ReportDashboard wurde von <strong>{scheduledAction.admin_user}</strong> ein {actionText} des Systems angefordert.
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              Ihnen verbleiben <strong>{Math.ceil(remainingSeconds / 60)} Minuten</strong> bis das System {actionVerb}.
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              Bitte sichern Sie alle begonnenen Reports und loggen Sie sich ordnungsgemäß aus, um Datenverluste zu verhindern.
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              Bei Fragen wenden Sie sich bitte an Ihren Administrator.
            </DialogContentText>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleUnderstood} 
            variant="contained" 
            color="primary"
            autoFocus
          >
            Verstanden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Persistenter Timer am unteren Bildschirmrand */}
      {timerVisible && remainingSeconds > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'var(--rd-danger)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '8px 8px 0 0',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            boxShadow: '0 -2px 10px rgba(0,0,0,0.3)'
          }}
        >
          {scheduledAction.action === 'restart' ? <RestartIcon /> : <ShutdownIcon />}
          <Typography variant="body2" fontWeight="bold">
            Backend-{actionText} in: {formatTime(remainingSeconds)}
          </Typography>
        </Box>
      )}

      {/* Backend Reconnecting Nachricht */}
      {reconnecting && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'var(--rd-accent)',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '8px 8px 0 0',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            boxShadow: '0 -2px 10px rgba(0,0,0,0.3)',
            minWidth: '300px'
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            Backend wird neu gestartet...
          </Typography>
          <LinearProgress 
            sx={{ 
              width: '100%',
              backgroundColor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white'
              }
            }} 
          />
          <Typography variant="caption">
            Bitte warten Sie einen Moment
          </Typography>
        </Box>
      )}

      {/* Backend wieder verfügbar */}
      {showReconnectedMessage && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'var(--rd-success)',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '8px 8px 0 0',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            boxShadow: '0 -2px 10px rgba(0,0,0,0.3)'
          }}
        >
          <CheckIcon />
          <Typography variant="body2" fontWeight="bold">
            System ist wieder verfügbar!
          </Typography>
        </Box>
      )}
    </>
  );
};

export default ScheduledActionNotification; 